<template>
    <div class="row mb-2">
        <div class="col-4">
            <label class="form-label">Depreciation Method</label>
            <v-select
                placeholder="Select Depreciation Method"
                v-model="depreciationMethod"
                :options="depIntervalMethods"
                :reduce="item => item.value"
                label="name"
                @option:selected="handleDepreciationMethodChange"
            />
        </div>
    </div>
      <div class="row mb-2">
        <div class="col-4">
            <label class="form-label">Depreciation Interval</label>
            <v-select
                placeholder="Select Depreciation Interval"
                v-model="depreciationInterval"
                :options="depIntervalIntervals"
                :reduce="item => item.value"
                label="name"
                @option:selected="handleDepreciationIntervalChange"
            />
        </div>
    </div>

    <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
        <label
            class="form-check-label"
            for="is-full-period-switch"
        >
            Is Full Period
        </label>
        <input
            v-model="isFullPeriod"
            class="form-check-input m-0"
            type="checkbox"
            role="switch"
            id="is-full-period-switch"
            @change="handleFullPeriodChange"
        />
    </div>

    <GlobalLoader />
</template>

<script setup>
import {inject, onMounted, ref} from "vue";
import handleCompany from "@/services/modules/company";
import handleInventory from "@/services/modules/inventory";
import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";

const depreciationMethod = ref(null)
const depreciationInterval = ref(null)
const isFullPeriod = ref(false)
const depIntervalMethods = ref([])
const depIntervalIntervals = ref([])
const { getCompanyPreferences, modifyCompanyPreference } = handleCompany();
const { fetchDepIntervalMethods } = handleInventory();
const $store = useStore();
const $route = useRoute();
const showSuccess = inject('showSuccess');
const companyId = $route.params.companyId;

const handleDepreciationMethodChange = (option) => {
    const data = {
        company_id: companyId,
        field: "depreciation_method",
        value: option.value,
    };
    handleUpdate(data);
}

const handleDepreciationIntervalChange = (option) => {
    const data = {
        company_id: companyId,
        field: "depreciation_interval",
        value: option.value,
    };
    handleUpdate(data);
}

const handleFullPeriodChange = (e) => {
    const nextValue = e.target.checked ? "1" : "0";
    const data = {
        company_id: companyId,
        field: "is_full_period_depreciation",
        value: nextValue,
    };
    handleUpdate(data);
}

const handleUpdate = (data) => {
    $store.state.loading = true;
    modifyCompanyPreference(data)
        .then((res) => {
            showSuccess(res.message);
        })
        .finally(() => ($store.state.loading = false));
}

const getInitialValue = (data, field) => {
    const item = data.find((item => item.field === field))
    return item ? item.value : null;
}

const parseBoolean = (v) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") return v.trim() === "1" || v.trim() === "true";
    return false;
}

const getDepIntervalMethods = () => {
    const query = `?company_id=${companyId}`;
    return fetchDepIntervalMethods(query).then((res) => {
        if (!res.status) return;
        depIntervalMethods.value = res.data.methods;
        depIntervalIntervals.value = res.data.intervals;
    })
}

const getPreferences = () => {
    const queryObject = {
        company_id: companyId,
        fields: 'depreciation_method,depreciation_interval,is_full_period_depreciation'
    }
    const query = '?' + new URLSearchParams(queryObject).toString();
    return getCompanyPreferences(query).then(res => {
        if (!res.data) return;
        const data = res.data;
        depreciationMethod.value = getInitialValue(data, 'depreciation_method')
        depreciationInterval.value = getInitialValue(data, 'depreciation_interval')
        const fullPeriod = getInitialValue(data, 'is_full_period_depreciation')
        if (fullPeriod === null) return;
        isFullPeriod.value = parseBoolean(fullPeriod)
    })
}

onMounted(() => {
    $store.state.loading = true
    Promise.all([getDepIntervalMethods(), getPreferences()])
        .finally(() => $store.state.loading = false);
});
</script>
